import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { authService, getUserFromToken } from './auth.service';
import { authQueryKeys } from './auth.keys';
import { tokenStorage } from '../../../services/api/tokenStorage';
import { notificationUtils } from '../../../services/api/notifications';
import {
	LoginRequest,
	LoginResponse,
	UserProfile,
	RefreshTokenResponse,
	UseAuthReturn,
} from '../types/auth.types';

// Type guard to check if data is a LoginResponse
const isLoginResponse = (data: any): data is LoginResponse => {
	return data && typeof data === 'object' && 'accessToken' in data;
};

// Query hook for checking authentication status
export const useAuthenticationQuery = () => {
	return useQuery({
		queryKey: authQueryKeys.isAuthenticated(),
		queryFn: () => tokenStorage.isLoggedIn(),
		staleTime: 1000 * 60 * 5, // 5 minutes
		refetchOnWindowFocus: true,
		refetchOnMount: true,
	});
};

// Query hook for getting user profile
export const useUserProfileQuery = () => {
	return useQuery({
		queryKey: authQueryKeys.profile(),
		queryFn: async (): Promise<UserProfile | null> => {
			// First try to get from localStorage
			const localProfile = tokenStorage.getUserProfile();
			if (localProfile) {
				// If it's a LoginResponse, extract the user property
				if (isLoginResponse(localProfile)) {
					return localProfile.user || null;
				}
				// Otherwise assume it's already a UserProfile
				return localProfile as UserProfile;
			}

			// If not in localStorage, fetch from API
			try {
				const response = await authService.getProfile();
				if (response.data) {
					tokenStorage.setUserProfile(response.data);
					// Extract user from LoginResponse if needed
					if (isLoginResponse(response.data)) {
						return response.data.user || null;
					}
					return response.data as UserProfile;
				}
				return null;
			} catch (error) {
				console.warn('useUserProfileQuery: API fetch failed, trying token extraction');

				// If API call fails, try to extract from token
				const token = tokenStorage.getAccessToken();
				if (token) {
					const userData = getUserFromToken(token);
					// Extract user from LoginResponse if needed
					if (userData && isLoginResponse(userData)) {
						return userData.user || null;
					}
					return userData as UserProfile | null;
				}
				return null;
			}
		},
		staleTime: 1000 * 60 * 5, // 5 minutes
		refetchOnWindowFocus: true,
		enabled: tokenStorage.isLoggedIn(), // Only run if user is logged in
	});
};

// Mutation hook for login
export const useLoginMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (credentials: LoginRequest): Promise<LoginResponse> => {
			try {
				const loginData = await authService.login(credentials);

				// Validate response
				if (!loginData) {
					throw new Error('Invalid login response from server');
				}

				if (!loginData.accessToken) {
					throw new Error('Login response missing access token');
				}

				// Store tokens and user profile
				tokenStorage.setTokens({
					accessToken: loginData.accessToken,
					refreshToken: undefined, // API doesn't provide refresh token
				});

				tokenStorage.setUserProfile(loginData);

				return loginData;
			} catch (error: any) {
				console.error('useLoginMutation: Login failed:', error);
				throw error;
			}
		},
		onSuccess: (data) => {
			// Invalidate and refetch auth-related queries
			queryClient.invalidateQueries({ queryKey: authQueryKeys.isAuthenticated() });
			queryClient.invalidateQueries({ queryKey: authQueryKeys.profile() });

			// Show success notification
			notificationUtils.success(`Welcome back, ${data.user?.nrp || data.user?.email}!`);
		},
		onError: (error: any) => {
			console.error('useLoginMutation: Login mutation failed:', error);

			// Clear any existing tokens on login failure
			tokenStorage.clearAuthData();
			queryClient.invalidateQueries({ queryKey: authQueryKeys.isAuthenticated() });

			// Show error notification
			const errorMessage = error?.message || 'Login failed. Please try again.';
			notificationUtils.error(errorMessage);
		},
	});
};

// Mutation hook for logout
export const useLogoutMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (): Promise<void> => {
			try {
				await authService.logout();
			} catch (error) {
				console.warn(
					'useLogoutMutation: Server logout failed, continuing with local cleanup:',
					error,
				);
			} finally {
				// Clear auth data from localStorage (enhanced)
				tokenStorage.clearAuthData();

				// Additional localStorage cleanup - remove any potential auth-related items
				if (typeof window !== 'undefined') {
					// Clear any remaining session or auth data that might be stored elsewhere
					const authRelatedKeys = ['user', 'session', 'login', 'authentication'];
					authRelatedKeys.forEach((keyPattern) => {
						for (let i = localStorage.length - 1; i >= 0; i--) {
							const key = localStorage.key(i);
							if (key && key.toLowerCase().includes(keyPattern)) {
								localStorage.removeItem(key);
							}
						}
					});
				}
			}
		},
		onSuccess: () => {
			// Clear ALL queries from cache - this is important for security
			queryClient.clear();

			// Force reset query client to initial state
			queryClient.resetQueries();

			// Specifically invalidate auth queries (in case clear() didn't work completely)
			queryClient.invalidateQueries({ queryKey: authQueryKeys.isAuthenticated() });
			queryClient.invalidateQueries({ queryKey: authQueryKeys.profile() });
			queryClient.invalidateQueries({ queryKey: authQueryKeys.all });

			// Remove queries from cache manually as backup
			queryClient.removeQueries({ queryKey: authQueryKeys.all });

			// Show success notification
			notificationUtils.success('You have been logged out successfully');
		},
		onError: (error: any) => {
			console.error('useLogoutMutation: Logout mutation failed:', error);

			// Even on error, perform complete cleanup
			tokenStorage.clearAuthData();
			queryClient.clear();
			queryClient.invalidateQueries({ queryKey: authQueryKeys.isAuthenticated() });

			// Show warning notification
			notificationUtils.warning('Logged out locally due to error');
		},
	});
};

// Mutation hook for token refresh
export const useRefreshTokenMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (): Promise<RefreshTokenResponse> => {
			const refreshToken = tokenStorage.getRefreshToken();
			if (!refreshToken) {
				throw new Error('No refresh token available');
			}

			const response = await authService.refresh(refreshToken);

			// Update stored tokens
			tokenStorage.setTokens({
				accessToken: response.data.accessToken,
			});

			// Update user profile if available
			if (response.data.user) {
				tokenStorage.setUserProfile(response.data.user);
			}
			return response.data;
		},
		onSuccess: () => {
			// Invalidate auth queries
			queryClient.invalidateQueries({ queryKey: authQueryKeys.isAuthenticated() });
			queryClient.invalidateQueries({ queryKey: authQueryKeys.profile() });
		},
		onError: (error: any) => {
			console.error('useRefreshTokenMutation: Refresh mutation failed:', error);

			// Clear auth data on refresh failure
			tokenStorage.clearAuthData();
			queryClient.invalidateQueries({ queryKey: authQueryKeys.isAuthenticated() });

			// Show error notification
			notificationUtils.error('Session expired. Please login again.');
		},
	});
};

// Main authentication hook - combines all auth functionality
export const useAuthQuery = (): UseAuthReturn => {
	const { data: isAuthenticated, isLoading: authLoading } = useAuthenticationQuery();
	const { data: userProfile, isLoading: profileLoading } = useUserProfileQuery();
	const loginMutation = useLoginMutation();
	const logoutMutation = useLogoutMutation();
	const refreshTokenMutation = useRefreshTokenMutation();

	const login = useCallback(
		async (credentials: LoginRequest) => {
			return loginMutation.mutateAsync(credentials);
		},
		[loginMutation],
	);

	const logout = useCallback(async () => {
		return logoutMutation.mutateAsync();
	}, [logoutMutation]);

	const refreshToken = useCallback(async () => {
		return refreshTokenMutation.mutateAsync();
	}, [refreshTokenMutation]);

	return {
		isAuthenticated: !!isAuthenticated,
		isLoading: authLoading || profileLoading,
		userProfile: userProfile ?? null,
		login,
		logout,
		refreshToken,
		loginError: loginMutation.error,
		logoutError: logoutMutation.error,
		refreshError: refreshTokenMutation.error,
		isLoginLoading: loginMutation.isPending,
		isLogoutLoading: logoutMutation.isPending,
		isRefreshLoading: refreshTokenMutation.isPending,
	};
};
